<template>
  <teleport :to="props.instance.teleportedTo">
    <span
      ref="focusTarget"
      tabindex="-1"
      :style="{
        '--el-overlay-color-lighter': 'rgb(15 23 42 / 0.5)',
      }"
    >
      <el-dialog
        v-model="isVisible"
        :style="{
          height: props.instance.dialogHeight,
          minHeight: props.instance.dialogMinHeight,
          minWidth: props.instance.dialogMinWidth,
          maxHeight: props.instance.dialogMaxHeight,
          maxWidth: props.instance.dialogMaxWidth,
        }"
        :append-to-body="false"
        append-to="body"
        :class="['flex flex-col', props.instance.dialogClass]"
        :modal="true"
        :show-close="props.instance.showClose"
        :align-center="props.instance.alignCenter"
        :destroy-on-close="true"
        :close-on-click-modal="props.instance.allowCloseByEscape"
        :close-on-press-escape="props.instance.allowCloseByEscape"
        :width="props.instance.dialogWidth"
        :before-close="props.instance.beforeClose"
        @opened="
          () => {
            props.instance.onOpen();

            dialogStoreV2.activateDialog(props.instance.identifier);
          }
        "
        @close="
          () => {
            dialogStoreV2.deactivateDialog(props.instance.identifier);

            props.instance.onClose();
          }
        "
        @closed="dialogStoreV2.destroyDialog(props.instance.identifier)"
      >
        <template #header>
          <div class="truncate text-base font-semibold">
            <component :is="props.instance.titleComponent!" />
          </div>
        </template>

        <template #default>
          <component :is="props.instance.bodyComponent!" />
        </template>

        <template
          v-if="props.instance.footerComponent !== null"
          #footer
        >
          <component :is="props.instance.footerComponent" />
        </template>
      </el-dialog>
    </span>
  </teleport>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  shallowRef,
  watch,
} from "vue";
import {
  ElDialog,
} from "element-plus";
import {
  useFocusTrap,
} from "@vueuse/integrations/useFocusTrap";
import type {
  MaybeElementRef,
} from "@vueuse/core";
import {
  type AbstractDialogInstanceType,
  DEFAULT_IDENTIFIER,
  useDialogStoreV2,
} from "./useDialogStoreV2";

type PropsType = {
  instance: AbstractDialogInstanceType;
};

const props = defineProps<PropsType>();

const focusTarget = shallowRef<HTMLSpanElement>();

const dialogStoreV2 = useDialogStoreV2();

const isVisible = computed<boolean>({
  get: () => {
    return props.instance.isVisible;
  },
  set: () => {},
});

props.instance.setupFn();

/* @todo: should be fixed case of using it as an input for :modal for dialogs of different size */
const isActive = computed<boolean>(() => {
  return dialogStoreV2.activeIdentifier === props.instance.identifier || dialogStoreV2.activeIdentifier === DEFAULT_IDENTIFIER;
});

const {
  activate,
  deactivate,
  pause,
  unpause,
} = useFocusTrap(focusTarget as MaybeElementRef<HTMLSpanElement>);

watch(isActive, async (isActiveValue) => {
  await nextTick();

  if (isActiveValue) {
    activate();
  } else {
    deactivate();
  }
}, {
  immediate: true,
});

watch(() => props.instance.activateFocusTrap, (activateFocusTrapValue) => {
  if (activateFocusTrapValue) {
    unpause();
  } else {
    pause();
  }
}, {
  immediate: true,
});
</script>

<style scoped>
:deep(.el-dialog__body) {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
